import { Component, Input } from '@angular/core';
import { BasicScatterChartConfig } from '@app/data/interface/widget-config';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { EChartsOption, SeriesOption } from 'echarts';
import { BaseFilteredEnvironmentalDataChartComponent } from '../base-filtered-environmental-data-chart-component';

/**
 * I've really tried to use types in this file but the inheritence is making it difficult.
 */

@Component({
  selector: 'app-basic-scatter-chart',
  templateUrl: './basic-scatter-chart.component.html',
  styleUrls: ['./basic-scatter-chart.component.scss'],
})
export class BasicScatterChartComponent extends BaseFilteredEnvironmentalDataChartComponent {
  @Input() config!: BasicScatterChartConfig;
  chartOptions: EChartsOption | null = null;
  id: string = 'basic-Scatter-chart-x';

  ngOnInit() {
    super.ngOnInit();
    this.id = `basic-scatter-chart-${this.config.dataConfig.widgetId}`;
    this.chartTitle = this.config.dataConfig.title || '';
    this.chartSubtitle = this.config.dataConfig.subtitle || '';
    this.domain = this.config.dataConfig.domain || '';
  }

  protected clearChartData(): void {
    this.chartOptions = null;
    this.hasData = false;
  }

  protected setWidgetData(): void {
    this.hasData = false;

    // If we don't have any data sources to look at, exit early.
    const preserveConfiguredLabels = Boolean(this.config?.dataConfig?.preserveConfiguredDataSourceLabels);
    const applicableXDataSource = this.selectDataSources([this.config.dataConfig.xDataSource], {
      preserveConfiguredLabels,
    });
    const applicableYDataSource = this.selectDataSources([this.config.dataConfig.yDataSource], {
      preserveConfiguredLabels,
    });

    // If there's nothing to display....
    if (applicableXDataSource.length === 0 || applicableYDataSource.length === 0) {
      return;
    }
    this.chartOptions = null;
    const newChartOptions: EChartsOption = this.config.echartsConfig;

    // Get the data
    forkJoin([
      this.chartService
        .getEnvironmentalDataForSourceAndAnalyteByDate(
          this.config.dataConfig.xDataSource.environmentalDataSourceIdentifier,
          this.config.dataConfig.xDataSource.analyteIdentifier,
          moment(this.startDate).format('YYYY-MM-DD'),
          moment(this.endDate).format('YYYY-MM-DD'),
          this.startDate === this.endDate ? 'hour' : 'day'
        )
        .pipe(take(1)),
      this.chartService
        .getEnvironmentalDataForSourceAndAnalyteByDate(
          this.config.dataConfig.yDataSource.environmentalDataSourceIdentifier,
          this.config.dataConfig.yDataSource.analyteIdentifier,
          moment(this.startDate).format('YYYY-MM-DD'),
          moment(this.endDate).format('YYYY-MM-DD'),
          this.startDate === this.endDate ? 'hour' : 'day'
        )
        .pipe(take(1)),
    ]).subscribe({
      next: (queryResponses: any) => {
        // only combine where we have the same date_time for the data point
        const dataMap: { [key: string]: { x?: any; y?: any } } = {};
        queryResponses[0]?.data?.forEach(
          (dataPoint: any) =>
            (dataMap[dataPoint.date_time] = { x: dataPoint[this.config.dataConfig.xDataSource.valueField], y: null })
        );
        queryResponses[1]?.data?.forEach(
          (dataPoint: any) =>
            (dataMap[dataPoint.date_time] = {
              ...dataMap[dataPoint.date_time],
              y: dataPoint[this.config.dataConfig.yDataSource.valueField],
            })
        );

        const scatterData = Object.values(dataMap)
          .filter((dataPoint: any) => !!dataPoint.x && !!dataPoint.y)
          .map((dataPoint: any) => [dataPoint.x, dataPoint.y]);

        if (scatterData.length < 2) {
          // No data
          this.chartOptions = {
            color: ['#6AB6B0'],
            title: {
              text: 'Not enough data is available for the selected date range.',
            },
          };
        } else {
          // Set the data.
          this.hasData = true;
          (newChartOptions.series as SeriesOption[])[0].data = scatterData;
          this.chartOptions = newChartOptions;
        }
      },
      error: (err: any) => {
        console.error(err);
        this.chartOptions = {
          color: ['#6AB6B0'],
          title: {
            text: 'No data is available.',
          },
        };
      },
    });
  }
}
